import { Tooltip } from "@hightouch/htui";
import { NATIVE_DELIVERY_PERMISSIONS } from "src/cep/permissions";
import { PermissionedMenuItem } from "src/components/permission";
import { type AmpLibraryFileFragment, useContentEmailQuery } from "src/graphql";
import { NativeDeliveryIcon } from "src/ui/icons/native-delivery";
import { useEntitlements } from "src/hooks/use-entitlement";
import { ND_LINKED_CAMPAIGN_REASON } from "src/pages/lifecycle-studio/emails/components/create-campaign-dialog";
import { ndAttachment } from "src/pages/lifecycle-studio/messages/nd-attachment";
import type { CreateCampaignTarget } from "src/pages/projects/library/canvas/use-create-campaign";

type Props = {
  file: AmpLibraryFileFragment;
  /** The email is read only while its menu is open, so idle tiles send no query. */
  isMenuOpen: boolean;
  onCreate: (target: CreateCampaignTarget) => void;
  /** Names the action where its surface calls it something other than the default. */
  label?: string;
};

/** Creates a Native Delivery campaign from the email version the tile shows; renders nothing for other files. */
export function CreateCampaignMenuItem({
  file,
  isMenuOpen,
  onCreate,
  label = "Export to Native Delivery",
}: Props) {
  const { data: entitlementsData } = useEntitlements(false);
  const isCepEnabled = Boolean(entitlementsData?.entitlements.cep);
  const emailId = file.email?.emailId;
  const { data: email } = useContentEmailQuery(
    { id: emailId ?? "" },
    {
      enabled: isCepEnabled && isMenuOpen && Boolean(emailId),
      select: (data) => data.content_emails_by_pk,
    },
  );

  if (!emailId || !isCepEnabled) return null;

  const versionId = file.versionId;
  const isLinked = ndAttachment(email) != null;
  const disabledReason = isLinked
    ? ND_LINKED_CAMPAIGN_REASON
    : !versionId
      ? "Available once the email is generated."
      : null;

  return (
    <Tooltip isDisabled={!disabledReason} message={disabledReason ?? ""}>
      <PermissionedMenuItem
        icon={NativeDeliveryIcon}
        // Also disabled while the email loads, so a linked email never reads as unlinked for that beat.
        isDisabled={Boolean(disabledReason) || !email}
        permission={NATIVE_DELIVERY_PERMISSIONS.draft}
        onClick={() => {
          if (versionId)
            onCreate({
              contentEmailId: emailId,
              contentEmailVersionId: versionId,
            });
        }}
      >
        {/* The default says Native Delivery until the nav consolidation renames it to Lifecycle Studio. */}
        {label}
      </PermissionedMenuItem>
    </Tooltip>
  );
}
